import { useState } from "react";
import { Camera, Calendar, MapPin, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

// Add photo URLs to `photos` (with optional captions) and they appear automatically.
export const PAST_EVENTS = [
  {
    id: "unfiltered-minds-charlotte-2026",
    name: "Unfiltered Minds",
    tagline: "A nationwide mental health open mic",
    date: "May 5, 2026",
    city: "Charlotte, NC",
    hosts: "Madina PLLC · Generational Health",
    photos: [],
  },
];

function PhotoPlaceholder({ index }) {
  return (
    <div
      className="aspect-[4/3] rounded-2xl border-2 border-dashed border-slate-300 bg-white/60 flex flex-col items-center justify-center text-muted-ink"
      data-testid={`past-event-placeholder-${index}`}
    >
      <Camera className="w-7 h-7 text-teal/70 mb-2" />
      <p className="text-xs font-semibold uppercase tracking-[0.18em]">Photos coming soon</p>
    </div>
  );
}

function PhotoTile({ photo, onOpen, testId }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group relative aspect-[4/3] rounded-2xl overflow-hidden bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal"
      data-testid={testId}
    >
      <img
        src={photo.url}
        alt={photo.caption || "Event photo"}
        loading="lazy"
        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
      />
      {photo.caption && (
        <span className="absolute inset-x-0 bottom-0 p-3 text-left text-sm text-white bg-gradient-to-t from-navy/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
          {photo.caption}
        </span>
      )}
    </button>
  );
}

function EventAlbum({ event, onOpen }) {
  const hasPhotos = event.photos.length > 0;
  return (
    <article className="gh-card !p-6 lg:!p-8" data-testid={`past-event-${event.id}`}>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h3 className="font-display text-3xl text-navy leading-tight">{event.name}</h3>
          <p className="text-teal italic">{event.tagline}</p>
        </div>
        <div className="flex flex-wrap gap-4 text-sm text-muted-ink">
          <span className="inline-flex items-center gap-1.5"><Calendar className="w-4 h-4 text-teal" />{event.date}</span>
          <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4 text-teal" />{event.city}</span>
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        {hasPhotos
          ? event.photos.map((p, i) => (
              <PhotoTile key={i} photo={p} onOpen={() => onOpen(event, i)} testId={`past-event-photo-${event.id}-${i}`} />
            ))
          : [0, 1, 2].map((i) => <PhotoPlaceholder key={i} index={i} />)}
      </div>
      <p className="mt-5 text-xs text-muted-ink uppercase tracking-[0.2em] font-bold">Hosted by {event.hosts}</p>
    </article>
  );
}

export const PastEventsGallery = () => {
  const [active, setActive] = useState(null);
  const photo = active && active.event.photos[active.index];

  return (
    <section className="py-20 lg:py-28 bg-cream" data-testid="past-events-section">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <p className="eyebrow mb-3">Past events</p>
        <h2 className="font-display text-4xl sm:text-5xl text-navy leading-[1.1] mb-12">
          Rooms we've <span className="hl-teal">already filled.</span>
        </h2>
        <div className="space-y-8">
          {PAST_EVENTS.map((e) => (
            <EventAlbum key={e.id} event={e} onOpen={(event, index) => setActive({ event, index })} />
          ))}
        </div>
      </div>

      <Dialog open={!!photo} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden bg-navy border-0" data-testid="past-event-lightbox">
          <DialogTitle className="sr-only">{photo?.caption || "Event photo"}</DialogTitle>
          {photo && <img src={photo.url} alt={photo.caption || "Event photo"} className="w-full max-h-[80vh] object-contain" />}
          {photo?.caption && <p className="px-6 py-4 text-white/85 text-sm">{photo.caption}</p>}
          <button
            type="button"
            onClick={() => setActive(null)}
            className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/15 text-white flex items-center justify-center"
            data-testid="past-event-lightbox-close"
          >
            <X className="w-4 h-4" />
          </button>
        </DialogContent>
      </Dialog>
    </section>
  );
};
